import { TableHelper } from "./TableHelper";
import { encodeTableRefrencing, needsDecoding } from "../../utils/util";
export class FillHelper {
  constructor(hfInstance, rootState, rootGetters, dispatch, currentSheet, initialRange, newRange) {
    this.hfInstance = hfInstance;
    this.rootState = rootState;
    this.rootGetters = rootGetters;
    this.dispatch = dispatch;
    this.currentSheet = currentSheet;
    this.initialRange = initialRange;
    this.newRange = newRange;
    this.ctrlKeyPressed = rootState.cells.rangeDragging.ctrlKey;
    this.direction = getChangeDirection(initialRange, newRange);

    this.reverse = this.direction === "up" || this.direction === "left";
    if (this.isHorizontal()) {
      this.newArrayLength = newRange[2] - newRange[0] + 1;
      this.initialRangeCount = initialRange[2] - initialRange[0] + 1;
    } else {
      this.newArrayLength = newRange[3] - newRange[1] + 1;
      this.initialRangeCount = initialRange[3] - initialRange[1] + 1;
    }
    // delta is the increment or decrement amount
    this.defaultDelta = this.initialRangeCount === 1 && this.ctrlKeyPressed ? (this.reverse ? -1 : 1) : undefined;
  }
  _getArrayFromRange(range) {
    const matrix = this.hfInstance.getRangeSerialized({
      start: { sheet: this.currentSheet.sheetId, col: range[0], row: range[1] },
      end: { sheet: this.currentSheet.sheetId, col: range[2], row: range[3] },
    });
    return matrix.flat();
  }
  shouldDuplicate() {
    return this.ctrlKeyPressed && this.initialRangeCount > 1;
  }
  isHorizontal() {
    return this.direction === "left" || this.direction === "right";
  }
  getDelta(data) {
    return this.defaultDelta || data[1] - data[0];
  }
  isNumberSeries(range) {
    const data = this._getArrayFromRange(range);
    return (data.length > 1 || this.ctrlKeyPressed) && data.every((item) => !isNaN(item) && item != null);
  }
  isTextSeries(range) {
    const data = this._getArrayFromRange(range);
    const arr = [];
    data.forEach((item) => arr.push(String(item).split(/(\d+)/)));
    const numberIndex = arr[0].findLastIndex((item) => !isNaN(item) && item !== "");
    return (
      /\d/.test(arr[0]) /* is there any digits in text  */ &&
      (arr.length >= 2 || this.ctrlKeyPressed) &&
      arr.every((item) =>
        item.every((subItem, index) => (index === numberIndex && !isNaN(subItem)) || subItem === arr[0][index])
      )
    );
  }
  getNewSeries(baseRange) {
    let data = this._getArrayFromRange(baseRange);

    const series = this.getNewSeriesByData(data);
    return this.isHorizontal() ? [series] : series.map((item) => [item]);
  }
  getNewTextSeries(baseRange) {
    const data = this._getArrayFromRange(baseRange);
    const firstElement = String(data[0]).split(/(\d+)/);
    const secondElement = String(data[data.length > 1 ? 1 : 0]).split(/(\d+)/);

    const numberIndex = firstElement.findLastIndex((item) => !isNaN(item) && item !== "");
    const newSeries = this.getNewSeriesByData([firstElement[numberIndex], secondElement[numberIndex]]);
    const series = new Array(this.newArrayLength).fill(0).map((item, index) => {
      firstElement[numberIndex] = newSeries[index];
      return firstElement.join("");
    });
    return this.isHorizontal() ? [series] : series.map((item) => [item]);
  }
  getNewSeriesByData = (data) => {
    if (this.reverse) data = data.reverse();
    let result = new Array(this.newArrayLength)
      .fill(0)
      .map((item, index) => +data[0] + index * this.getDelta(data) + "");
    if (this.reverse) result.reverse();
    return result;
  };
  async getTableRefrenceSeries(baseRange, newRange, series, resultsSeries) {
    if (!series) return;
    series = series[0]; // Assuming you're working with the first part of a 2D series
    const tableHelper = new TableHelper(this.hfInstance, this.rootState, this.rootGetters);
    const results = [[]]; // 2D array to store results
    let cellIds = this.rootGetters["cells/rangeIds"](baseRange);
    let newCellIds = this.rootGetters["cells/rangeIds"](newRange);
    if (this.direction === "left" || this.direction === "up") {
      newCellIds = newCellIds.reverse();
      cellIds = cellIds.reverse();
      series = series.reverse();
      resultsSeries = resultsSeries.reverse();
    }

    let text;

    for (let i = 0; i < newCellIds.length; i++) {
      let initialRangeIndex = i % cellIds.length;
      let steps = 0; // for refrences there is no vertical series
      let cellId = newCellIds[i];
      let absoluteNameRanges, updatedValues, decoded;
      if (this.direction === "up" || this.direction === "down") {
        const initialCellId = cellIds[initialRangeIndex];
        ({ absoluteNameRanges, updatedValues } = await tableHelper.findAndShiftNamedRanges(
          text,
          this.direction,
          initialCellId,
          steps
        ));
        decoded = resultsSeries[i];

        if (!results[i]) {
          results.push([decoded]);
        } else {
          results[i].push(decoded);
        }
      } else {
        initialRangeIndex = i % cellIds.length;
        steps = parseInt(i / cellIds.length);
        text = series[initialRangeIndex];
        const newCellIndex = cellIds.length * steps + initialRangeIndex;
        cellId = newCellIds[newCellIndex];
        const initialCellId = cellIds[initialRangeIndex];
        ({ absoluteNameRanges, updatedValues } = await tableHelper.findAndShiftNamedRanges(
          text,
          this.direction,
          initialCellId,
          steps
        ));

        // Initialize the original and decoded texts
        let originalText = resultsSeries[steps * cellIds.length + initialRangeIndex]; // Original text with global replacements
        decoded = resultsSeries[steps * cellIds.length + initialRangeIndex]; // Decoded text with local replacements

        // Apply the updates based on the returned updated values
        updatedValues.forEach(({ updatedNameRange, updatedLocalNameRange, originalNameRange }) => {
          // Replace the original name range with the updated name range in the original text
          originalText = originalText.replace(originalNameRange, updatedNameRange);
          // Replace the original name range with the local name range in the decoded text
          decoded = decoded.replace(originalNameRange, updatedLocalNameRange);
        });

        // Store the original modified text
        results[0].push(originalText);
      }
      // Encode the decoded text with the absolute name ranges

      let convertedList = [];
      absoluteNameRanges.forEach((nameRange, index) => {
        const converted = this.rootGetters["table/getLocalNameRangeByDefault"](nameRange.reference);
        decoded = decoded.replace(nameRange, converted);
        convertedList.push({ reference: converted, index: nameRange.index });
      });
      absoluteNameRanges = convertedList;
      decoded = encodeTableRefrencing(decoded, absoluteNameRanges);

      // using convertToRefrenceFormat for handleing normal ranges for inRowNameRanges
      const result = tableHelper.convertToRefrenceFormat({ text: decoded ?? "", cellId, absoluteNameRanges });
      let convertedText = result.text;
      if (needsDecoding(convertedText)) {
        await this.dispatch(
          "cells/editCellMetaData",
          { id: cellId, key: "formula", value: convertedText },
          { root: true }
        );
      }
    }
    if (this.direction === "left") {
      results[0].reverse();
    }
    if (this.direction === "up") {
      results.reverse();
    }
    return results; // Return the 2D array with transformed names
  }
}
const getChangeDirection = (initialRange, newRange) => {
  // Same Column
  if (newRange[0] == initialRange[0] && newRange[2] == initialRange[2]) {
    if (newRange[3] > initialRange[3]) return "down";
    else return "up";
  }
  // Same Row
  else if (newRange[1] == initialRange[1] && newRange[3] == initialRange[3]) {
    if (newRange[2] > initialRange[2]) return "right";
    else return "left";
  }
};
// what is the maximum number of columns with data when series is rows
// what is the minimum number of rows with data when series is columns
export const getMaxDataInRange = (range, seriesIn, hfInstance, currentSheet) => {
  const isRowSeries = seriesIn === "Rows";
  let maxData = isRowSeries ? range[0] : range[1];

  for (let i = range[1]; i <= range[3]; i++) {
    for (let j = range[0]; j <= range[2]; j++) {
      const cellValue = hfInstance.getCellValue({ sheet: currentSheet.sheetId, col: j, row: i });
      if (cellValue != null && (isRowSeries ? j > maxData : i > maxData)) {
        maxData = isRowSeries ? j : i;
      }
    }
  }
  return maxData;
};

export const getInitialRange = (range, maxData, seriesIn) => {
  return seriesIn === "Rows" ? [range[0], range[1], maxData, range[3]] : [range[0], range[1], range[2], maxData];
};
